/* eslint-disable no-unused-vars */
import React, { useCallback, memo } from 'react'; // Import memo and useCallback
import { ReactComponent as Logo } from 'assets/img/brand/logo.svg';
import { ReactComponent as Loading } from 'assets/img/loading.svg';

const SpoolVendorReport = memo(({ targetRef, vendorReport }) => {
    const getRiskColorClass = useCallback((riskLevel) => {
        switch (riskLevel?.toLowerCase()) {
            case 'low':
                return 'bg-[#55BE8B]';
            case 'medium':
                return 'bg-[#F2C021]';
            case 'high':
                return 'bg-[#FF5449]';
            default:
                return 'bg-white';
        }
    }, []);

    if (!vendorReport) {
        return (
            <div className="spool-vendor-report-container display-flex items-center justify-center">
                <p>Loading vendor report...</p>
            </div>
        );
    }

    if (vendorReport?.error) {
        return (
            // Using CSS classes defined in SpoolVendorReport.css for layout
            <div className="w-[746.76px] h-[1180px] display-flex items-center justify-center">
                {/* Using CSS class for color */}
                <p className="text-[#FF5449]">Error: {vendorReport?.error}</p>
            </div>
        );
    }

    return (
        <main ref={targetRef} className="report-main font-poppins">
            {/* --- Header Section/ First Page --- */}
            <section className="w-[746.76px] h-[1180px] relative">
                <div className="absolute top-10 left-0">
                    <Logo className="w-[120px]" />
                </div>

                <div className="absolute top-1/2 text-center left-1/2 -translate-y-1/2 -translate-x-1/2 transform">
                    <h2 className="text-[#334155] font-bold text-[36px]">VENDOR REPORT</h2>
                    <p className="text-[#475569] font-medium text-xl mt-9">{vendorReport?.report_date ?? 'N/A'}</p>
                    <h3 className="mt-20 text-[#475569] font-semibold text-[28px]">
                        {vendorReport?.merchant_name ?? 'N/A'}
                    </h3>
                </div>
            </section>

            {/* --- Vendor List Section --- */}
            {vendorReport?.vendor_data && vendorReport.vendor_data.length > 0 ? (
                vendorReport.vendor_data?.map((vendor, index) => (
                    <section
                        key={vendor?.id ?? index}
                        className={`vendor-section !w-full p-0 relative ${index > 0 && 'mt-20'}`}
                    >
                        <div className={`relative left-0 h-full w-full`}>
                            <h3 className="text-[#2B3674] font-semibold text-xl ml-2">
                                {index + 1}. {vendor?.vendor_name ?? 'N/A'}
                            </h3>
                            <p className="my-4 text-[#475569] font-semibold text-[17px]">Vendor Details</p>

                            <div className="flex gap-6">
                                <div className="flex-1 border-t border-[#F1F5F9]">
                                    {/* Vendor Name */}
                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                        <p className="w-[35%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                            Vendor name
                                        </p>
                                        <div className="flex-1 relative flex items-center">
                                            <p className="px-2 text-[#395BA9] font-normal text-sm -mt-[15px]">
                                                {vendor?.vendor_name ?? 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Vendor Type */}
                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                        <p className="w-[35%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                            Vendor type
                                        </p>
                                        <div className="flex-1 relative flex items-center">
                                            <p className="px-2 text-[#395BA9] font-normal text-sm -mt-[15px]">
                                                {vendor?.vendor_type ?? 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Vendor Email */}
                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                        <p className="flex-1 border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                            Vendor email
                                        </p>
                                        <div className="w-[65%] relative flex items-center">
                                            <p className="px-2 text-[#395BA9] font-normal text-sm -mt-[15px] text-wrap">
                                                {vendor?.vendor_email?.slice(0, 26)}
                                                {vendor?.vendor_email?.length > 25 && (
                                                    <>
                                                        <br /> {vendor?.vendor_email?.slice(26)}
                                                    </>
                                                )}
                                                {!vendor?.vendor_email && 'N/A'} {/* Handle null/undefined email */}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Data Shared */}
                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                        <p className="w-[35%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                            Data shared
                                        </p>
                                        <div className="flex-1 relative flex items-center">
                                            <p className="px-2 text-[#395BA9] font-normal text-sm -mt-[15px] capitalize">
                                                {vendor?.vendor_data_shared ?? 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Products */}
                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                        <p className="w-[35%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                            Products
                                        </p>
                                        <div className="flex-1 relative flex items-center">
                                            <p className="px-2 text-[#395BA9] font-normal text-sm -mt-[15px] capitalize">
                                                {vendor?.vendor_products ?? 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-1 border-t border-[#F1F5F9]">
                                    {/* Description */}
                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                        <p className="w-[35%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                            Description of service
                                        </p>
                                        <div className="flex-1 relative flex items-center">
                                            <p className="px-2 text-[#395BA9] font-normal text-sm -mt-[15px] capitalize">
                                                {vendor?.vendor_service_desc ?? 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Intended Risk */}
                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                        <p className="w-[35%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                            Intended risk level
                                        </p>
                                        <div className="flex-1 relative flex items-center">
                                            <div className="flex-1">
                                                <div className="w-fit flex mx-2 px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                    <div>
                                                        {/* Using the updated getRiskColorClass function */}
                                                        <div
                                                            className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                vendor?.intended_risk_level
                                                            )}`}
                                                        />
                                                    </div>
                                                    <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                        {vendor?.intended_risk_level ?? 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Risk Alert */}
                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                        <p className="w-[35%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                            Risk alert level
                                        </p>
                                        <div className="flex-1 relative flex items-center">
                                            <p className="px-2 text-[#395BA9] font-normal text-sm -mt-[15px]">
                                                {vendor?.vendor_risk_alert_level ?? 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* --- Questionnaires Section --- */}
                            {!vendor.questionnaires || vendor.questionnaires.length === 0 ? (
                                <div className="flex justify-center items-center flex-col gap-2 mb-4 mt-2">
                                    <div className="w-[316px]">
                                        <Loading className="w-[170px]" />
                                    </div>
                                    <p className="text-[#395BA9] font-medium text-lg -mt-[10px]">
                                        No Questionnaire response from this vendor yet
                                    </p>
                                </div>
                            ) : (
                                vendor.questionnaires.map((q, qIndex) =>
                                    q.questionnaire_has_responded ? (
                                        <div key={q?.id ?? qIndex}>
                                            <p className="mt-1 relative mb-2 pb-2 text-[#475569] font-semibold text-[17px]">
                                                {q?.questionnaire_name ?? 'Questionnaire'}
                                                <br /> {/* Keep original <br /> */}
                                            </p>

                                            <div className="flex gap-6">
                                                {/* Left Questionnaire Column */}
                                                <div className="flex-1 border-t border-[#F1F5F9]">
                                                    {/* Business Continuity */}
                                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                                        <p className="w-[60%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                                            Business Continuity / Disaster Recovery
                                                        </p>
                                                        <div className="flex-1 relative flex items-center">
                                                            <p className="w-[66px] px-2 text-[#395BA9] font-medium text-sm -mt-[15px]">
                                                                {q?.bus_cont_info?.percent ?? '-'}
                                                            </p>
                                                            <div className="flex-1">
                                                                <div className="w-fit flex px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                                    <div>
                                                                        {/* Using the updated getRiskColorClass function */}
                                                                        <div
                                                                            className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                                q?.bus_cont_info?.risk_level
                                                                            )}`}
                                                                        />
                                                                    </div>
                                                                    <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                                        {q?.bus_cont_info?.risk_level ?? 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Incident Response */}
                                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                                        <p className="w-[60%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                                            Incident Response
                                                        </p>
                                                        <div className="flex-1 relative flex items-center">
                                                            <p className="w-[66px] px-2 text-[#395BA9] font-medium text-sm -mt-[15px]">
                                                                {q?.incident_res_info?.percent ?? '-'}
                                                            </p>
                                                            <div className="flex-1">
                                                                <div className="w-fit flex px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                                    <div>
                                                                        <div
                                                                            className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                                q?.incident_res_info?.risk_level
                                                                            )}`}
                                                                        />
                                                                    </div>
                                                                    <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                                        {q?.incident_res_info?.risk_level ?? 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Risk Management */}
                                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                                        <p className="w-[60%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                                            Risk Management/Auditing /Regulatory Compliance
                                                        </p>
                                                        <div className="flex-1 relative flex items-center">
                                                            <p className="w-[66px] px-2 text-[#395BA9] font-medium text-sm -mt-[15px]">
                                                                {q?.risk_mgt_info?.percent ?? '-'}
                                                            </p>
                                                            <div className="flex-1">
                                                                <div className="w-fit flex px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                                    <div>
                                                                        <div
                                                                            className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                                q?.risk_mgt_info?.risk_level
                                                                            )}`}
                                                                        />
                                                                    </div>
                                                                    <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                                        {q?.risk_mgt_info?.risk_level ?? 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Availability */}
                                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                                        <p className="w-[60%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                                            Availability
                                                        </p>
                                                        <div className="flex-1 relative flex items-center">
                                                            <p className="w-[66px] px-2 text-[#395BA9] font-medium text-sm -mt-[15px]">
                                                                {q?.avail_info?.percent ?? '-'}
                                                            </p>
                                                            <div className="flex-1">
                                                                <div className="w-fit flex px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                                    <div>
                                                                        <div
                                                                            className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                                q?.avail_info?.risk_level
                                                                            )}`}
                                                                        />
                                                                    </div>
                                                                    <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                                        {q?.avail_info?.risk_level ?? 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right Questionnaire Column */}
                                                <div className="flex-1 border-t border-[#F1F5F9]">
                                                    {/* Org Info Sec */}
                                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                                        <p className="w-[60%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                                            Organizational Information Security
                                                        </p>
                                                        <div className="flex-1 relative flex items-center">
                                                            <p className="w-[66px] px-2 text-[#395BA9] font-medium text-sm -mt-[15px]">
                                                                {q?.org_info_sec_info?.percent ?? '-'}
                                                            </p>
                                                            <div className="flex-1">
                                                                <div className="w-fit flex px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                                    <div>
                                                                        <div
                                                                            className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                                q?.org_info_sec_info?.risk_level
                                                                            )}`}
                                                                        />
                                                                    </div>
                                                                    <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                                        {q?.org_info_sec_info?.risk_level ?? 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* General Security */}
                                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                                        <p className="w-[60%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                                            General Security
                                                        </p>
                                                        <div className="flex-1 relative flex items-center">
                                                            <p className="w-[66px] px-2 text-[#395BA9] font-medium text-sm -mt-[15px]">
                                                                {q?.gen_sec_info?.percent ?? '-'}
                                                            </p>
                                                            <div className="flex-1">
                                                                <div className="w-fit flex px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                                    <div>
                                                                        <div
                                                                            className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                                q?.gen_sec_info?.risk_level
                                                                            )}`}
                                                                        />
                                                                    </div>
                                                                    <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                                        {q?.gen_sec_info?.risk_level ?? 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Network Security */}
                                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                                        <p className="w-[60%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                                            Network Security
                                                        </p>
                                                        <div className="flex-1 relative flex items-center">
                                                            <p className="w-[66px] px-2 text-[#395BA9] font-medium text-sm -mt-[15px]">
                                                                {q?.net_sec_info?.percent ?? '-'}
                                                            </p>
                                                            <div className="flex-1">
                                                                <div className="w-fit flex px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                                    <div>
                                                                        <div
                                                                            className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                                q?.net_sec_info?.risk_level
                                                                            )}`}
                                                                        />
                                                                    </div>
                                                                    <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                                        {q?.net_sec_info?.risk_level ?? 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Security Monitoring */}
                                                    <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                                        <p className="w-[60%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                                            Security Monitoring
                                                        </p>
                                                        <div className="flex-1 relative flex items-center">
                                                            <p className="w-[66px] px-2 text-[#395BA9] font-medium text-sm -mt-[15px]">
                                                                {q?.sec_mon_info?.percent ?? '-'}
                                                            </p>
                                                            <div className="flex-1">
                                                                <div className="w-fit flex px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                                    <div>
                                                                        <div
                                                                            className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                                q?.sec_mon_info?.risk_level
                                                                            )}`}
                                                                        />
                                                                    </div>
                                                                    <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                                        {q?.sec_mon_info?.risk_level ?? 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* AML */}
                                                    {q?.aml_info && (
                                                        <div className="flex border-b border-[#F1F5F9] overflow-hidden">
                                                            <p className="w-[60%] border-r border-[#F1F5F9] text-[#64748B] text-[13px] leading-5 font-medium py-[15px] px-1 -mt-[15px]">
                                                                AML
                                                            </p>
                                                            <div className="flex-1 relative flex items-center">
                                                                <p className="w-[66px] px-2 text-[#395BA9] font-medium text-sm -mt-[15px]">
                                                                    {q.aml_info?.percent ?? '-'}
                                                                </p>
                                                                <div className="flex-1">
                                                                    <div className="w-fit flex px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                                        <div>
                                                                            <div
                                                                                className={`w-2 h-2 rounded-full ${getRiskColorClass(
                                                                                    q.aml_info?.risk_level
                                                                                )}`}
                                                                            />
                                                                        </div>
                                                                        <p className="text-[#64748B] font-medium text-xs -mt-[15px]">
                                                                            {q.aml_info?.risk_level ?? 'N/A'}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Scorecard Total */}
                                            <div className="flex gap-2 items-center p-0 h-[42px] justify-center text-[#64748B] border-y-[1px] border-solid border-[#E2E8F0]">
                                                <strong className="-mt-3.5 font-semibold inline-block text-[#64748B] text-[13px] pr-5 h-fit py-1.5">
                                                    Score card total
                                                </strong>
                                                <div className="flex gap-1 items-center p-0">
                                                    <strong className="block font-semibold -mt-4 h-fit text-[16px] text-[#395BA9]">
                                                        {q?.questionnaire_scorecard ?? '-'}%
                                                    </strong>
                                                    <div className="w-fit flex h-fit px-2 py-1.5 gap-1 border border-[#E2E8F0] rounded items-center">
                                                        {/* Using the updated getRiskColorClass function */}
                                                        <div
                                                            className={`w-2 h-2 inline-block rounded-full ${getRiskColorClass(
                                                                q?.questionnaire_risk_level
                                                            )}`}
                                                        />
                                                        <p className="text-[#64748B] font-medium h-fit inline-blocks text-xs -mt-[15px]">
                                                            {q?.questionnaire_risk_level
                                                                ? `${q.questionnaire_risk_level} risk`
                                                                : 'N/A'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <p className="pl-2 text-[12px] p-0 h-[42px] flex items-center border-l-[1px] border-[#E2E8F0] ">
                                                    {' '}
                                                    <span className="-mt-3">Answered by:&nbsp;</span>
                                                    <span className="-mt-3 text-[#395BA9]">
                                                        {q?.recipient_email ?? 'N/A'}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    ) : null
                                )
                            )}
                        </div>
                    </section>
                ))
            ) : (
                <section className="vendor-section">
                    <p className="text-center my-4">No vendor data available.</p>
                </section>
            )}
        </main>
    );
});

SpoolVendorReport.displayName = 'SpoolVendorReport';
export default SpoolVendorReport;
